// Follow the submitted variant, including option apps that change the form input.
if (!customElements.get('mc-variant-stock')) customElements.define('mc-variant-stock', class extends HTMLElement {
 connectedCallback() {
  this.form = document.getElementById(this.dataset.form);
  if (!this.form) return;
  this.observed = this.dataset.variant;
  this.check = () => {
   const id = String(this.form.elements.namedItem('id')?.value || '');
   if (id === this.observed) return;
   this.observed = id;
   this.refresh(id);
  };
  this.form.addEventListener('change', this.check);
  this.form.addEventListener('variant:change', this.check);
  // Some option apps set .value without emitting a change event.
  this.poll = setInterval(this.check, 400);
  this.check();
 }
 disconnectedCallback() {
  clearInterval(this.poll);
  this.controller?.abort();
  this.form?.removeEventListener('change', this.check);
  this.form?.removeEventListener('variant:change', this.check);
 }
 async refresh(id) {
  this.controller?.abort();
  this.replaceChildren(); // Never leave the previous variant's scarcity message visible.
  if (!/^\d+$/.test(id)) return;
  const controller = this.controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20000);
  try {
   const url = new URL(this.dataset.productUrl, window.location.origin);
   url.searchParams.set('variant', id);
   url.searchParams.set('section_id', this.dataset.section);
   const response = await fetch(url, {signal: controller.signal, cache: 'no-store'});
   if (!response.ok) throw new Error('Stock unavailable');
   const html = new DOMParser().parseFromString(await response.text(), 'text/html');
   const match = Array.from(html.querySelectorAll('mc-variant-stock')).find(el => el.dataset.form === this.dataset.form && el.dataset.variant === id);
   if (this.isConnected && this.observed === id && !controller.signal.aborted && match) {
    this.replaceChildren(...Array.from(match.childNodes).map(node => node.cloneNode(true)));
    this.dataset.variant = id;
   }
  } catch (_) {
   // Keep the alert hidden if the selected variant's inventory cannot be verified.
  } finally { clearTimeout(timer); }
 }
});

// Native Mamacita personalisation and matching accessories.
(() => {
 const remembered = new Map();
 if (customElements.get('mc-product-options')) return;
 class MamacitaOptions extends HTMLElement {
  connectedCallback() {
   this.controller = new AbortController();
   const signal = this.controller.signal;
   this.variants = JSON.parse(this.querySelector('[data-mc-variants]').textContent);
   this.form = this.closest('form');
   if (!this.form) return;
   this.stateKey = this.dataset.form;
   const saved = remembered.get(this.stateKey) || {};
   ['name','font','thread'].forEach(key => { const field=this.querySelector('[data-'+key+']'); if(field && saved[key] != null) field.value=saved[key]; });
   const toggle=this.querySelector('[data-personalise]'); if(toggle && !toggle.disabled) toggle.checked=!!saved.enabled;
   this.querySelectorAll('[data-set-variant]').forEach(select=>{ const available=Array.from(select.options).find(o=>!o.disabled); if(available)select.value=available.value; else select.closest('.mc-set-row').hidden=true; });
   this.addEventListener('click',event=>{const choice=event.target.closest('[data-font-choice]');if(choice && this.contains(choice)){this.querySelector('[data-font]').value=choice.dataset.fontChoice;this.remember();this.sync();}},{signal});
   this.addEventListener('input',()=>{this.remember();this.sync();},{signal});
   this.addEventListener('change',()=>{this.remember();this.sync();},{signal});
   this.form.addEventListener('variant:change',()=>this.sync(),{signal});
   this.form.addEventListener('change',()=>this.sync(),{signal});
   this.form.addEventListener('submit',e=>{
    this.sync(); const name=this.querySelector('[data-name]');
    if(name && !name.disabled){ name.value=name.value.trim(); name.setCustomValidity(name.value ? '' : 'Please enter the name or initials for personalisation.'); }
    if(!this.form.checkValidity()){ e.preventDefault();e.stopImmediatePropagation();this.form.reportValidity(); }
   },{capture:true,signal});
   this.form.addEventListener('formdata',e=>this.prepare(e.formData),{signal});
   this.sync();
  }
  disconnectedCallback(){this.controller?.abort();}
  remember(){ const state={enabled:!!this.querySelector('[data-personalise]')?.checked}; ['name','font','thread'].forEach(key=>{state[key]=this.querySelector('[data-'+key+']')?.value;}); remembered.set(this.stateKey,state); const name=this.querySelector('[data-name]'); if(name)name.setCustomValidity(''); }
  sync(){
   const id=String(this.form.elements.namedItem('id')?.value || '');
   const variant=this.variants.find(v=>String(v.id)===id);
   const kind=this.dataset.kind;
   this.enabled=kind==='washbag' || ((kind==='robe'||kind==='bonnie') && !!this.querySelector('[data-personalise]')?.checked) || ((kind==='hat'||kind==='mask') && /personalis/i.test(variant?.title||''));
   const panel=this.querySelector('.mc-personalisation');
   if(panel && (kind==='hat'||kind==='mask'))panel.hidden=!this.enabled;
   const fields=this.querySelector('[data-personalisation-fields]');
   if(fields){fields.hidden=!this.enabled;fields.querySelectorAll('input,select').forEach(f=>f.disabled=!this.enabled);}
   this.querySelectorAll('[data-font-choice]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.fontChoice===this.querySelector('[data-font]')?.value)));
   const extra=this.selectedExtras();
   const qty=Number(this.form.elements.namedItem('quantity')?.value || 1);
   const sum=extra.reduce((s,e)=>s+e.price,0);
   const money=n=>new Intl.NumberFormat(document.documentElement.lang||'en-GB',{style:'currency',currency:this.dataset.currency||'GBP'}).format(n/100);
   this.querySelector('[data-options-total]').textContent=sum ? 'With your selections: '+money(((variant?.price||0)+sum)*qty) : '';
  }
  selectedExtras(){
   const extras=[];
   if((this.dataset.kind==='robe'||this.dataset.kind==='bonnie') && this.enabled && this.dataset.fee)extras.push({id:this.dataset.fee,price:Number(this.dataset.feePrice),fee:true});
   this.querySelectorAll('.mc-set-row').forEach(row=>{if(!row.hidden && row.querySelector('[data-set-check]').checked){const s=row.querySelector('select');const opt=s.selectedOptions[0];if(opt && !opt.disabled)extras.push({id:s.value,price:Number(opt.dataset.price)});}});
   return extras;
  }
  prepare(fd){
   this.sync(); const extras=this.selectedExtras(); if(!extras.length)return;
   const id=fd.get('id'); const quantity=fd.get('quantity')||'1';
   const entries=Array.from(fd.entries());
   for(const [key,value] of entries){if(key==='id'||key==='quantity'||key==='selling_plan'||key.startsWith('properties[')){fd.delete(key);fd.append('items[0]['+(key.startsWith('properties[')?'properties]['+key.slice(11):key+']'),value);}}
   fd.set('items[0][id]',id);fd.set('items[0][quantity]',quantity);
   extras.forEach((extra,index)=>{
    const base='items['+(index+1)+']';fd.set(base+'[id]',extra.id);fd.set(base+'[quantity]',quantity);
    if(extra.fee){
     fd.set(base+'[parent_id]',id);
     fd.set(base+'[properties][For]',this.form.querySelector('[data-name]').value.trim());
     fd.set(base+'[properties][Font]',this.form.querySelector('[data-font]').value);
     fd.set(base+'[properties][_mc_embroidery]','true');
     fd.set('items[0][properties][_mc_embroidery_variant]',extra.id);
    }
   });
  }
 }
 customElements.define('mc-product-options',MamacitaOptions);
})();
